/**
 * MAGSDK basic implementation of update plugin.
 *
 * @author <bas.jsdev@gmail.com>
 */


'use strict';

var gettextWrapper = require('mag-gettext'),
    Emitter        = require('cjs-emitter'),
    tools          = require('./tools'),
    updater, daemon, waitForReboot, gettext;


/**
 * Update manager. Listening daemon for records changes and emitting corresponding events to application.
 * Can emit events: progress, state, add, remove.
 *
 * @constructor
 * @extends Emitter
 */
function Client () {
    var self    = this,
        trigger = {};

    Emitter.call(this);

    this.list = [];

    Object.defineProperties(this, {
        autoUpdateEnabled: {
            get: function () { return !daemon.stalkerMode; },
            set: function () { return false; }
        },
        updateImageUrl: {
            get: function () { return daemon.updateImageUrl; },
            set: function () { return false; }
        },
        standAloneMode: {
            get: function () { return !!daemon.standAloneMode; },
            set: function () { return false; }
        }
    });

    if ( !daemon.stalkerMode ) {
        Object.defineProperties(this, {
            betaUpdate: {
                get: function () {
                    return daemon.betaUpdate;
                },
                set: function ( flag ) {
                    //console.log('daemon.betaUpdate set to ' + flag);
                    daemon.betaUpdate = flag;
                    daemon.storage.setItem('betaUpdate', flag);
                    daemon.list = [];
                    waitForReboot = true;
                    // check if one of old beta images became actual
                    daemon.sync();
                }
            },
            frequency: {
                get: function () {
                    return daemon.frequency;
                },
                set: function ( time ) {
                    //console.log('daemon.frequency set to ' + time);
                    daemon.frequency = time;
                    daemon.storage.setItem('frequency', time);
                    window.clearInterval(daemon.checkTimerId);
                    if ( daemon.frequency ) {
                        daemon.checkTimerId = window.setInterval(daemon.sync, daemon.frequency);
                    }
                    daemon.list = [];
                    waitForReboot = true;
                    daemon.sync();
                }
            }
        });
    }

    trigger.onListUpdate = function ( list ) {
        self.list = list;

        try {
            self.emit('listChange', list);
        } catch ( error ) {
            console.log('update plugin: error in one of listChange listeners. ' + error);
        }
    };

    trigger.onHardUpdate = function ( data ) {
        try {
            self.emit('hardUpdate', data);
        } catch ( error ) {
            console.log('update plugin: error in one of hardUpdate listeners. ' + error);
        }
    };

    daemon.triggers.push(trigger);

    /**
     * Stop this client and remove all it listeners. Use it for cleanup before application exit.
     */
    this.destroy = function () {
        daemon.triggers.splice(daemon.triggers.indexOf(trigger), 1);
        this.events = {};
        this.list = [];
    };
}

Client.prototype = Object.create(Emitter.prototype);
Client.prototype.constructor = Client;


/**
 * Description for codes.
 *
 * @type {Object}
 */
Client.prototype.constants = {
    NEVER: 0,
    WEEKLY: 7 * 24 * 60 * 60 * 1000,
    // setInterval and setTimeout can't be set with delay larger than 2147483647 (int)
    MONTHLY: 24 * 24 * 60 * 60 * 1000
};

/**
 * Text representations of update statuses
 */
Client.prototype.statusCodes = {
    1: 'Signature init error',
    2: 'Wrong device model',
    3: 'Section size exceeds partition size on FLASH',
    4: 'Required FLASH section not found. Aborting update',
    5: 'Updating kernel',
    6: 'Updating image',
    7: 'Internal error',
    8: 'Inspecting firmware',
    9: 'Updating environment variables',
    10: 'Updating Bootstrap section',
    11: 'Skipping Bootstrap section',
    12: 'Updating User FS section',
    13: 'Skipping User FS section',
    14: 'Updating second boot',
    15: 'Updating logotype',
    16: 'Update finished OK',
    17: 'Wrong signature',
    18: 'Erasing flash section',
    19: 'Flash write error',
    20: 'File write error',
    21: 'Idle',
    22: 'Invalid file header',
    23: 'Inspecting update file',
    24: 'File check finished',
    25: 'File not found',
    26: 'Initialising',
    27: 'Read error'
};

/**
 * Callback for updater.verify method.
 *
 * @callback updater.verifyCallback
 *
 * @param {Object} [error] error data
 * @param {Object} error.code error code
 * @param {Object} error.message error text message
 * @param {Object} [config] new image information
 * @param {string} [config.date] date
 * @param {string} [config.description] description
 * @param {string} [config.version] version
 */

/**
 * Get firmware meta data.
 * @param {Object} config check options
 * @param {string} config.uri path to firmware image (http or file)
 * @param {updater.verifyCallback} callback callback
 */
Client.prototype.check = function ( config, callback ) {
    // dead end states
    var crashCodes = [1, 2, 3, 4, 7, 17, 19, 20, 22, 25, 27/*, 28, 29*/],
        readyCode  = 21,
        timerId;

    //console.log('config.uri:' + config.uri);
    if ( !config.uri ) {
        callback({code: 0, message: 'Not set path to image'}, null);

        return;
    }

    stbUpdate.startCheck(config.uri);

    timerId = window.setInterval(function () {
        var status = stbUpdate.getStatus();

        //console.log('check status:' + Client.prototype.statusCodes[status]);
        if ( status === readyCode ) {
            window.clearInterval(timerId);
            callback(null, {
                date: tools.toDate(stbUpdate.getImageDateStr()),
                name: stbUpdate.getImageVersionStr(),
                descr: stbUpdate.getImageDescStr(),
                url: config.uri,
                type: ''
            });
        } else if ( crashCodes.indexOf(status) !== -1 ) {
            window.clearInterval(timerId);
            callback({
                code: status,
                message: Client.prototype.statusCodes[status]
            }, null);
        }
    }, 100);
};

/**
 * Start update. After successful update device will be rebooted.
 *
 * @param {Object} [config] update options
 * @param {string} [config.uri] path to image (in none, default update uri would be used)
 */
Client.prototype.start = function ( config ) {
    // dead end states
    var crashCodes = [1, 2, 3, 4, 7, 17, 19, 20, 22, 25, 27/*, 28, 29*/],
        self       = this,
        // get active NAND number
        activeBank = stbUpdate.getActiveBank(),
        // get device model
        modelTemp  = gSTB.RDir('Model').toUpperCase(),
        progress   = 0,
        oldStatus  = stbUpdate.getStatus(),
        realActiveBank, timerId;

    config.uri = config.uri || gSTB.GetDefaultUpdateUrl();

    //console.log('url for update:' + config.uri);
    if ( !config.uri ) {
        self.emit('error', {code: 1, text: 'Wrong update url'});

        return;
    }

    // check device model
    if ( modelTemp !== 'MAG250' && modelTemp !== 'MAG270' && modelTemp !== 'MAG275' ) {
        // check memory banks on old devices
        if ( activeBank !== -1 && stbUpdate.GetFlashBankCount() !== 2 ) {
            self.emit('error', {code: 30, text: 'Unable to update active memory bank'});

            return;
        }
    }

    // check if active bank corrupted and we loaded in emergency mode (logs caught in magcore-app-portal-loader)
    realActiveBank = stbStorage.getItem('nandEmergencyLoadingLogs');
    if ( realActiveBank ) {
        realActiveBank = (JSON.parse(realActiveBank) || '').bootmedia;
        if ( realActiveBank === 'bank0' ) { activeBank = 0; }
        if ( realActiveBank === 'bank1' ) { activeBank = 1; }
    }

    // write to non active nand
    if ( activeBank === 0 ) {
        stbUpdate.startUpdate(1, config.uri);
    } else {
        stbUpdate.startUpdate(0, config.uri);
    }

    timerId = window.setInterval(function () {
        var status = stbUpdate.getStatus();

        //console.log('update status:' + Client.prototype.statusCodes[status]);
        if ( crashCodes.indexOf(status) === -1 ) {
            // send progress
            self.emit('progress', {old: progress, now: stbUpdate.getPercents()});
            progress = stbUpdate.getPercents();
            // send new status (if changed)
            if ( status !== oldStatus ) {
                self.emit('stateChange', {
                    old: {code: oldStatus, text: Client.prototype.statusCodes[oldStatus]},
                    now: {code: status, text: Client.prototype.statusCodes[status]}
                });
                oldStatus = status;
            }
        } else {
            window.clearInterval(timerId);
            self.emit('error', {code: status, text: Client.prototype.statusCodes[status]});
        }
    }, 100);
};


/**
 * Current image info.
 */
Client.prototype.current = {date: '', version: '', description: ''};

/**
 * Default path to image on usb.
 */
Client.prototype.defaultPathToImageOnUsb = '/' + (gSTB.RDir('Model') || 'mag').toLowerCase() + '/imageupdate';


/**
 * Watching for changes in server update list and if there are some - triggering corresponding callbacks in clients.
 * Can't be reached from application scope.
 *
 * @namespace
 */
daemon = {
    /**
     * In stand alone mode we called in separate window by stbUpdate.startAutoUpdate(imageUrl, checkVersion); function
     * and there is no portal - only update page and core
     */
    standAloneMode: false,
    /**
     * In stalker mode all auto update triggers are controlled by server
     */
    stalkerMode: false,
    /**
     * Id from update setInterval function
     *
     * @type {number}
     */
    checkTimerId: 0,
    /**
     * Interval between updates (24 days)
     *
     * @type {number}
     */
    frequency: 24 * 24 * 60 * 60 * 1000,
    /**
     * List of clients listeners. For example to make first client update progress for recordItem and emit event
     * 'progress' for his app, you should call daemon.triggers[0].onProgress(recordItem).
     *
     * @type {Array}
     */
    triggers: [],
    /**
     * Hash to connect raw records data and records objects in clients. Otherwise on each update operation we should
     * use multiple cycles to build connections between raw data and record objects and only than apply changes.
     *
     * @type {Object}
     */
    actualVersions: [],
    betaUpdate: false,
    updateListUrl: '',
    updateImageUrl: '',
    list: [],
    sync: function () {
        var client, hashData, currTimeStamp;

        if ( !daemon.updateListUrl ) {
            console.log('updateListUrl not set - can\'t check new versions');

            return;
        }

        if ( daemon.stalkerMode ) {
            // stalker portal update scheme
            client = new Client();
            client.check({uri: daemon.updateListUrl}, function ( error, data ) {
                if ( error ) {
                    console.log('Can\'t check new image for update: ' + error.message);

                    return;
                }
                if ( !data || !data.date ) {
                    console.log('Wrong image headers: ' + data);

                    return;
                }
                data.date = tools.toDate(data.date);
                daemon.list = [data];
                daemon.triggers.forEach(function ( listener ) {
                    if ( typeof listener.onListUpdate === 'function' ) {
                        listener.onListUpdate(daemon.list.slice());
                    }
                });
            });
        } else {
            // embedded portal update scheme
            //console.log('check update list at server: ' + daemon.updateListUrl + '?mac=' + gSTB.GetDeviceMacAddress());
            // modelNameEx + currentVersion + serialNumber + macAddr + currentLang + portalUrl + porta1 + portal2 + userAgent + current_t_wo_sec
            currTimeStamp = Math.round(Date.now() / 1000);
            hashData = (gSTB.GetDeviceModelExt ? gSTB.GetDeviceModelExt() : gSTB.GetDeviceModel());
            hashData += gSTB.GetDeviceImageDesc() + gSTB.GetDeviceSerialNumber() + gSTB.GetDeviceMacAddress();
            hashData += (window.core.environment.language || 'en') + location.href;
            hashData += (window.core.environment.portal1 || '') + (window.core.environment.portal2 || '');
            hashData += navigator.userAgent + (currTimeStamp - (currTimeStamp % 60));
            tools.ajax('get', daemon.updateListUrl + '?mac=' + gSTB.GetDeviceMacAddress(), function ( data ) {
                var rawData, newest, changed, contain, ind, ind2;

                try {
                    rawData = JSON.parse(data);
                } catch ( error ) {
                    console.log('parsing err: ' + error);
                    rawData = [];
                } finally {
                    rawData = rawData || [];
                }

                if ( rawData.length === daemon.list.length ) {
                    for ( ind = 0; ind < rawData.length; ind++ ) {
                        contain = false;
                        for ( ind2 = 0; ind2 < daemon.list.length; ind2++ ) {
                            if ( rawData[ind].name === daemon.list[ind2].name ) {
                                contain = true;
                                break;
                            }
                        }
                        if ( !contain ) {
                            changed = true;
                            break;
                        }
                    }
                } else {
                    changed = true;
                }

                if ( changed ) {
                    daemon.list = [];
                    rawData.forEach(function ( item ) {
                        item.date = tools.toDate(item.date);
                        daemon.list.push(item);
                        // find fresh image with newest version and, if auto update activated, start it with this image
                        if ( (item.date.getTime() || 0) > (Client.prototype.current.date.getTime() || 0) &&
                            (item.type === 'release' || (updater.betaUpdate && item.type === 'beta')) ) {
                            newest = newest || item;
                            if ( (item.date.getTime() || 0) > (newest.date.getTime() || 0) ) {
                                newest = item;
                            }
                        }
                    });

                    if ( newest && (daemon.frequency !== Client.prototype.constants.NEVER) ) {
                        if ( waitForReboot ) {
                            window.core.notify({
                                title: gettext('New software available. Device will be updated after reboot.'),
                                icon: 'alert',
                                type: 'warning'
                            });
                        } else if ( window.core.taskManager && window.core.taskManager.launcher &&
                            window.core.taskManager.launcher.core && window.core.taskManager.launcher.core.ready ) {
                            //console.log('core ready');
                            //console.log('intent action: update');
                            waitForReboot = true;
                            window.core.intent({
                                action: 'update',
                                data: newest
                            }, function ( error ) {
                                if ( error ) { console.log('error in update intent: ' + error); }
                            });
                        } else {
                            //console.log('core not ready'); // try again in 10 sec
                            setTimeout(daemon.sync, 10000);
                            daemon.list = [];
                        }
                    }

                    daemon.triggers.forEach(function ( listener ) {
                        if ( typeof listener.onListUpdate === 'function' ) {
                            listener.onListUpdate(daemon.list.slice());
                        }
                    });
                }
            }, {
                'X-CurrentVersion': gSTB.GetDeviceImageDesc(),
                'X-SerialNumber': gSTB.GetDeviceSerialNumber(),
                'X-MacAddress': gSTB.GetDeviceMacAddress(),
                'X-ModelNameEx': gSTB.GetDeviceModelExt ? gSTB.GetDeviceModelExt() : gSTB.GetDeviceModel(),
                'X-Language': window.core.environment.language || 'en',
                'X-Portal1': window.core.environment.portal1 || '',
                'X-Portal2': window.core.environment.portal2 || '',
                'X-PortalUrl': location.href,
                'X-Hash': gSTB.GetHashVersion1(hashData, '')
            });
        }
    },
    init: function ( config ) {
        var updateListUrl, updateImageUrl, frequency,
            env      = window.core.environment,
            storage  = config.storage,
            currDate = config.storage.getItem('currentDate');

        function applyCurrentStorageData () {
            Client.prototype.current.date = new Date(storage.getItem('currentDate'));
            Client.prototype.current.version = storage.getItem('currentVersion');
            Client.prototype.current.description = storage.getItem('currentDescription');

            daemon.frequency = Number(storage.getItem('frequency'));
            daemon.updateListUrl = storage.getItem('updateListUrl');
            daemon.updateImageUrl = storage.getItem('updateImageUrl');
            daemon.betaUpdate = storage.getItem('betaUpdate');
        }

        daemon.storage = storage;
        // 1) migration from old to new portal
        // 2) current firmware image was updated so now we need to update it's info in storage
        if ( !currDate || ((tools.toDate(env.Image_Date)).getTime() !== (new Date(currDate)).getTime()) ) {
            //console.log('env:' + (tools.toDate(env.Image_Date)).getTime() + ', storage:' + (new Date(currDate)).getTime());
            // Start migration. Save data to new device storage
            //console.log('Start migration. Save data to new device storage');
            // save correct date string to storage
            storage.setItem('currentDate', (tools.toDate(env.Image_Date)).toString());
            storage.setItem('currentVersion', env.Image_Version);
            storage.setItem('currentDescription', env.Image_Desc);
            frequency = Number(env.autoupdate_cond) === 2 ? Client.prototype.constants.NEVER : Client.prototype.constants.MONTHLY;
            storage.setItem('frequency', storage.getItem('frequency') || frequency);
            storage.setItem('betaUpdate', storage.getItem('betaUpdate') || !!Number(env.betaupdate_cond));
            // mode specific options
            if ( daemon.stalkerMode ) {
                // stalker portal update scheme
                window.core.backend.once('ready', function () {
                    updateListUrl = window.core.backend.settings.update_url || '';
                    updateImageUrl = window.core.backend.settings.update_url || '';
                    storage.setItem('updateListUrl', updateListUrl);
                    storage.setItem('updateImageUrl', updateImageUrl);
                    applyCurrentStorageData();
                });
            } else {
                // embedded portal update scheme
                updateListUrl = env.autoupdateURL || (config.options && config.options.url ? config.options.url : '');
                updateImageUrl = env.update_url || gSTB.GetDefaultUpdateUrl();
                storage.setItem('updateListUrl', updateListUrl);
                storage.setItem('updateImageUrl', updateImageUrl);
                applyCurrentStorageData();
            }
        } else {
            applyCurrentStorageData();
        }

        daemon.checkMinistraAutoUpdate();
    },
    /**
     * check if we were called in separate window by stbUpdate.startAutoUpdate(imageUrl, checkVersion); function
     *
     * @return {boolean} result
     */
    isStandAlone: function () {
        var keys = tools.parseUri(window.location).queryKey;

        if ( (keys.CheckVersion === 'true' || keys.CheckVersion === 'false') && keys.url ) {
            daemon.standAloneMode = {
                check: keys.CheckVersion === 'true',
                url: keys.url
            };
        }
        //console.log('daemon.standAloneMode:' + JSON.stringify(daemon.standAloneMode));

        return !!daemon.standAloneMode;
    },
    runStandAloneMode: function () {
        var client;

        if ( daemon.standAloneMode.check ) {
            client = new Client();
            client.check({uri: daemon.standAloneMode.url}, function ( error, data ) {
                if ( error || !data || !data.date || (data.date || '').toString() === 'Invalid Date' ) {
                    console.log('can\'t check this image, close update window');
                    window.stbWebWindow.messageSend(1, 'AutoUpdateWindow:closed', '');
                    window.stbWebWindow.close();

                    return;
                }
                data.date = tools.toDate(data.date);
                if ( ((data.date.getTime() || 0) > (Client.prototype.current.date.getTime() || 0)) && data.type === 'release' ) {
                    daemon.triggers.forEach(function ( listener ) {
                        if ( typeof listener.onHardUpdate === 'function' ) {
                            listener.onHardUpdate(data);
                        }
                    });
                    window.stbWindowMgr.windowShow(window.stbWebWindow.windowId());
                    window.stbWebWindow.messageSend(1, 'AutoUpdateWindow:opened', '');
                } else {
                    console.log('up to date, close update window');
                    window.stbWebWindow.messageSend(1, 'AutoUpdateWindow:closed', '');
                    window.stbWebWindow.close();
                }
            });
        } else {
            //console.log('send intent');
            daemon.triggers.forEach(function ( listener ) {
                if ( typeof listener.onHardUpdate === 'function' ) {
                    listener.onHardUpdate({name: '', url: daemon.standAloneMode.url, type: '', date: '', descr: ''});
                }
            });
            window.stbWindowMgr.windowShow(window.stbWebWindow.windowId());
            window.stbWebWindow.messageSend(1, 'AutoUpdateWindow:opened', '');
        }
    },

    checkMinistraAutoUpdate: function () {
        var autoUpdate = core.backend.settings.autoupdate,
            updateUrl = core.backend.settings['update_url'],
            deviceImageVersion = core.device.imageVersion,
            deviceImageDescription = core.device.imageDescription,
            deviceHardwareVersion = core.device.versionHardware,
            prefix = autoUpdate['prefix'],
            requiredImageVersion = autoUpdate['require_image_version'],
            requiredImageDate = autoUpdate['require_image_date'],
            imageVersionContains = autoUpdate['image_version_contains'],
            imageDescriptionContains = autoUpdate['image_description_contains'],
            hardwareVersionContains = autoUpdate['hardware_version_contains'],
            // eslint-disable-next-line
            imageDate = top.gSTB.RDir('ImageDate').trim(),
            urlParts,
            url;

        switch ( autoUpdate['update_type'] ) {
            case 'reboot_dhcp':
                // eslint-disable-next-line
                top.gSTB.ExecAction('RebootDHCP');

                break;
            case 'http_update':
                if ( !updateUrl ) {
                    return;
                }

                if (
                    (
                        (requiredImageVersion && requiredImageVersion !== deviceImageVersion)
                        || (requiredImageDate && requiredImageDate !== imageDate)
                    )
                    && (
                        (!imageVersionContains || imageVersionContains === deviceImageVersion)
                        && (!imageDescriptionContains || deviceImageDescription.indexOf(imageDescriptionContains) > -1)
                        && (!hardwareVersionContains || deviceHardwareVersion.indexOf(hardwareVersionContains) > -1)
                    )
                ) {
                    console.log('need autoupdate', autoUpdate);
                    url = updateUrl;

                    if ( prefix ) {
                        urlParts = updateUrl.split('/');
                        urlParts[urlParts.length - 1] = prefix + urlParts[urlParts.length - 1];
                        url = urlParts.join('/');
                    }
                    setTimeout(function () {
                        top.core.intent({
                            action: 'update',
                            data: {
                                url: url
                            }
                        });
                    }, 1000);
                }

                break;
        }
    }
};


module.exports = {
    onInit: function ( config, callback ) {
        gettextWrapper.load({
            name: window.core.environment.language,
            path: config.path + 'lang'
        }, function () {
            var coreDaemon = new (require('cjs-emitter'))();

            gettext = gettextWrapper.gettext;
            // wait until system be ready
            coreDaemon.addListener('system:start', function () {
                if ( daemon.isStandAlone() ) {
                    daemon.stalkerMode = false;
                    daemon.init(config);
                } else {
                    // wait until core.backend plugin will be ready
                    daemon.stalkerMode = !!window.MODE_STALKER;
                    daemon.init(config);
                    if ( !daemon.stalkerMode && daemon.frequency ) {
                        clearInterval(daemon.checkTimerId);
                        daemon.checkTimerId = window.setInterval(daemon.sync, daemon.frequency);
                    }
                }
            });
            callback(null, coreDaemon);
        });
    },
    onAppInit: function ( config, callback ) {
        //console.log('updater_onAppInit');
        callback(null, new Client());
        if ( daemon.standAloneMode ) {
            daemon.runStandAloneMode();
        } else {
            daemon.sync();
        }
    },
    onSettingsInit: function ( config, callback ) {
        callback(null, {
            options: [
                {
                    id: 'updateManager',
                    name: gettext('Software Update'),
                    description: gettext('Select the update method, enable/disable automatic updates, update to the beta version'),
                    icon: 'theme-icon-update',
                    events: {
                        click: function () {
                            //console.log('intent action: update');
                            window.core.intent({
                                action: 'update',
                                data: {
                                    closeCallback: config.api.actions.view.reset /*config.api.actions.resetView*/
                                }
                            }, function ( error ) {
                                if ( error ) { console.log('error in update intent: ' + error); }
                            });
                        }
                    },
                    render: config.api.renders.listItem,
                    parent: 'system'
                }
            ]
        });
    }
};
